import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ChevronLeft, 
  ChevronRight, 
  SlidersHorizontal, 
  Plus, 
  RefreshCw, 
  ShieldAlert, 
  Wrench, 
  RotateCcw, 
  ClipboardCheck,
  Search
} from "lucide-react";
import ManageStockModal from "./ManageStockModal";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const StockAdjustment = ({ title: propTitle, type: propType }) => {
  const location = useLocation();

  // Deduce configuration based on path or props
  const config = useMemo(() => {
    if (propTitle && propType) return { title: propTitle, type: propType, icon: <SlidersHorizontal className="w-6 h-6 text-[#b07d17]" /> };

    if (location.pathname.includes("/damage-loss")) {
      return {
        title: "Damage & Loss Management",
        type: "Damage",
        icon: <ShieldAlert className="w-6 h-6 text-rose-600" />,
        desc: "Track broken, expired, or transit-damaged items written off from sellable inventory.",
      };
    }
    if (location.pathname.includes("/repair-management")) {
      return {
        title: "Repair & Refurbishment",
        type: "Repair",
        icon: <Wrench className="w-6 h-6 text-amber-600" />,
        desc: "Items sent for artisan restoration, polishing, or defect repair before return to stock.",
      };
    }
    if (location.pathname.includes("/returns-replacement")) {
      return {
        title: "Returns & Replacement Inventory",
        type: "Return",
        icon: <RotateCcw className="w-6 h-6 text-purple-600" />,
        desc: "Customer return inspections and artisan replacement inventory reconciliations.",
      };
    }
    if (location.pathname.includes("/stock-count")) {
      return {
        title: "Stock Count & Physical Reconciliation",
        type: "Adjustment",
        icon: <ClipboardCheck className="w-6 h-6 text-emerald-600" />,
        desc: "Cycle counting audits and physical inventory discrepancy adjustments.",
      };
    }

    return {
      title: "Stock Adjustment",
      type: "Adjustment",
      icon: <SlidersHorizontal className="w-6 h-6 text-[#b07d17]" />,
      desc: "Manual stock increases, decreases, and variance corrections across all warehouses.",
    };
  }, [propTitle, propType, location.pathname]);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Modals for New Adjustment
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    try {
      const typeQuery = config.type ? `&type=${config.type}` : "";
      const { data } = await axios.get(`${API}/inventory/activities?page=${page}&limit=10${typeQuery}`);
      let acts = data.activities || [];
      if (searchTerm.trim()) {
        const s = searchTerm.toLowerCase();
        acts = acts.filter(a => 
          (a.productName || "").toLowerCase().includes(s) ||
          (a.sku || "").toLowerCase().includes(s) ||
          (a.transactionId || "").toLowerCase().includes(s)
        );
      }
      setActivities(acts);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [config.type, page, searchTerm]);

  useEffect(() => {
    fetchAdjustments();
  }, [fetchAdjustments]);

  // Load products & warehouses for new adjustment modal
  const openNewAdjustment = async () => {
    try {
      const [pRes, wRes] = await Promise.all([
        axios.get(`${API}/inventory/items?limit=100`),
        axios.get(`${API}/inventory/warehouses`),
      ]);
      setProductsList(pRes.data.products || []);
      setWarehouses(wRes.data.warehouses || []);
      if (pRes.data.products?.length > 0) {
        setSelectedProduct(pRes.data.products[0]);
        setIsModalOpen(true);
      } else {
        toast.error("No products available to adjust");
      }
    } catch (err) {
      console.error("Error loading products for adjustment:", err);
      toast.error("Failed to load products");
    }
  };

  return (
    <section className="min-h-screen bg-[#f3f4f6] p-4 sm:p-6 font-sans">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>Admin</span>
            <span>›</span>
            <span>Inventory</span>
            <span>›</span>
            <span className="text-[#b07d17] font-semibold">{config.title}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-philosopher text-gray-900 flex items-center gap-3 font-bold">
            <span className="p-2 rounded-xl bg-gradient-to-br from-[#131921] to-[#232f3e] text-[#febd69] shadow-md">
              {config.icon}
            </span>
            {config.title}
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">{config.desc}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAdjustments}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 font-medium shadow-sm transition"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={openNewAdjustment}
            className="bg-[#b07d17] hover:bg-[#8c6312] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-md text-sm font-semibold"
          >
            <Plus size={16} /> New Adjustment
          </button>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-4 flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search SKU, Product Name, Txn ID..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
          />
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
        </div>
        <div className="text-xs text-gray-500">
          Showing <strong>{activities.length}</strong> recorded audit transactions
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#f8f9fa] border-b border-gray-200 text-gray-700">
              <tr>
                <th className="px-4 py-3.5 font-semibold">Date & Time</th>
                <th className="px-4 py-3.5 font-semibold">Transaction ID</th>
                <th className="px-4 py-3.5 font-semibold">Product Title</th>
                <th className="px-4 py-3.5 font-semibold">SKU</th>
                <th className="px-4 py-3.5 font-semibold text-right">Qty Delta</th>
                <th className="px-4 py-3.5 font-semibold">Warehouse</th>
                <th className="px-4 py-3.5 font-semibold">Reason / Audit Note</th>
                <th className="px-4 py-3.5 font-semibold">Performed By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-16 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#b07d17] mx-auto mb-2" />
                    <span>Loading adjustment records...</span>
                  </td>
                </tr>
              ) : activities.length > 0 ? (
                activities.map((act) => (
                  <tr key={act._id} className="hover:bg-gray-50/80 transition">
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(act.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#007185]">
                      {act.transactionId || "--"}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium line-clamp-1 max-w-[200px]">
                      {act.productName || act.product?.name?.en || "--"}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{act.sku || act.product?.sku || "--"}</td>
                    <td className="px-4 py-3 font-bold text-right text-sm">
                      <span className={act.quantity > 0 ? "text-emerald-700" : act.quantity < 0 ? "text-rose-700" : "text-gray-700"}>
                        {act.quantity > 0 ? `+${act.quantity}` : act.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{act.warehouse || "Bhubaneswar (Main)"}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[200px]" title={act.note}>
                      {act.note || "--"}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{act.performedBy || "Admin"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-4 py-16 text-center text-gray-500">
                    <SlidersHorizontal className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-base font-semibold text-gray-700">No {config.title.toLowerCase()} recorded yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click "New Adjustment" above to perform an inventory count adjustment or write-off.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && activities.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white text-sm">
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Adjustment Modal */}
      {isModalOpen && selectedProduct && (
        <ManageStockModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedProduct(null); }}
          product={selectedProduct}
          warehouses={warehouses}
          onStockUpdated={() => fetchAdjustments()}
        />
      )}
    </section>
  );
};

export default StockAdjustment;
